import React, { useEffect, useRef, useState } from "react";
import EmojiPicker, { EmojiStyle, Theme } from "emoji-picker-react";
import { AnimatePresence, motion } from "framer-motion";
import { Send, Smile, Sparkles, Palette, Mic, Clock, Eye, Wand2, X, Loader2, Plus, Image as ImageIcon } from "lucide-react";
import { VibePicker } from "./VibePicker";
import DoodleCanvas from "./DoodleCanvas";
import VoiceRecorder from "./VoiceRecorder";
import ScheduleDialog from "./ScheduleDialog";
import MobileToolsSheet from "./MobileToolsSheet";
import api, { formatApiError } from "@/lib/api";

const TONES = [
  { key: "formal", label: "Resmi" },
  { key: "playful", label: "Esprili" },
  { key: "romantic", label: "Romantik" },
  { key: "apologetic", label: "Özürlü" },
  { key: "professional", label: "Profesyonel" },
];

export default function MessageComposer({ onSend, onTyping }) {
  const [text, setText] = useState("");
  const [showEmoji, setShowEmoji] = useState(false);
  const [showVibe, setShowVibe] = useState(false); // desktop popover
  const [showTone, setShowTone] = useState(false); // desktop popover
  const [vibe, setVibe] = useState(null);
  const [whisper, setWhisper] = useState(false);
  const [doodleOpen, setDoodleOpen] = useState(false);
  const [voiceOpen, setVoiceOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduledFor, setScheduledFor] = useState(null);
  const [toneLoading, setToneLoading] = useState(false);
  const [mobileToolsOpen, setMobileToolsOpen] = useState(false);
  const [mobileVibeOpen, setMobileVibeOpen] = useState(false);
  const [mobileToneOpen, setMobileToneOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const typingTimeout = useRef(null);
  const pickerRef = useRef(null);
  const vibeRef = useRef(null);
  const toneRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) setShowEmoji(false);
      if (vibeRef.current && !vibeRef.current.contains(e.target)) setShowVibe(false);
      if (toneRef.current && !toneRef.current.contains(e.target)) setShowTone(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const handleChange = (e) => {
    setText(e.target.value);
    onTyping?.(true);
    clearTimeout(typingTimeout.current);
    typingTimeout.current = setTimeout(() => onTyping?.(false), 1500);
  };

  const resetExtras = () => {
    setVibe(null);
    setWhisper(false);
    setScheduledFor(null);
  };

  const handleSend = () => {
    const value = text.trim();
    if (!value) return;
    onSend({
      content: value,
      content_type: "text",
      vibe,
      whisper,
      scheduled_for: scheduledFor,
    });
    setText("");
    resetExtras();
    onTyping?.(false);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const submitDoodle = async (dataUrl) => {
    try {
      const { data } = await api.post("/uploads", { data_url: dataUrl, kind: "doodle" });
      onSend({ content: "", content_type: "doodle", media_url: data.url, vibe });
    } catch (e) {
      alert("Yükleme başarısız: " + formatApiError(e));
    }
    setDoodleOpen(false);
    resetExtras();
  };

  const submitVoice = async (dataUrl, duration) => {
    try {
      const { data } = await api.post("/uploads", { data_url: dataUrl, kind: "voice" });
      onSend({ content: "", content_type: "voice", media_url: data.url, media_duration: duration, vibe });
    } catch (e) {
      alert("Yükleme başarısız: " + formatApiError(e));
    }
    setVoiceOpen(false);
    resetExtras();
  };

  // ---- Photo / Video upload -------------------------------------------
  // Opens the native file picker (accept="image/*,video/*"), reads the file
  // as a base64 data URL, POSTs it to /api/uploads and sends the resulting
  // media_url as an image/video message. Chunk-free — the /api/uploads
  // handler already accepts data URLs.
  const openFilePicker = () => fileInputRef.current?.click();

  const readFileAsDataURL = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const handleFileChosen = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file
    if (!file) return;
    // Photos and short videos — bigger files should use a dedicated
    // chunked upload, but for MVP a single POST works up to ~25 MB.
    const isImage = file.type.startsWith("image/");
    const isVideo = file.type.startsWith("video/");
    if (!isImage && !isVideo) {
      alert("Sadece fotoğraf veya video seçebilirsin");
      return;
    }
    // Client-side size guard so we don't hit backend 413/422. Kept slightly
    // below the backend's 28MB base64 ceiling (≈ 20MB raw) so photos never
    // fail and users still get reasonably long videos through.
    const maxMb = isImage ? 12 : 18;
    if (file.size > maxMb * 1024 * 1024) {
      alert(`Dosya çok büyük — en fazla ${maxMb} MB olabilir`);
      return;
    }
    setUploading(true);
    try {
      const dataUrl = await readFileAsDataURL(file);
      const kind = isImage ? "image" : "video";
      const { data } = await api.post("/uploads", { data_url: dataUrl, kind });
      onSend({
        content: "",
        content_type: kind,
        media_url: data.url,
        vibe,
      });
    } catch (err) {
      alert("Yükleme başarısız: " + formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const shiftTone = async (tone) => {
    if (!text.trim()) return;
    setToneLoading(true);
    try {
      const { data } = await api.post("/ai/tone-shift", { text: text.trim(), tone });
      if (data?.text) setText(data.text);
    } catch (e) {
      alert(formatApiError(e));
    } finally {
      setToneLoading(false);
      setShowTone(false);
      setMobileToneOpen(false);
    }
  };

  const hasExtras = vibe || whisper || scheduledFor;
  const hasText = text.trim().length > 0;

  return (
    <>
      {hasExtras && (
        <div className="px-3 md:px-4 pt-2 flex items-center gap-2 flex-wrap border-t-[3px] border-[#0A0A0A] bg-white" data-testid="composer-extras-bar">
          {vibe && (
            <span className="text-[11px] uppercase font-black tracking-widest bg-[#CCFF00] border-hard-2 rounded-full px-2 py-0.5 flex items-center gap-1">
              vibe: {vibe} <button onClick={() => setVibe(null)} data-testid="clear-vibe" aria-label="clear"><X strokeWidth={3} className="w-3 h-3" /></button>
            </span>
          )}
          {whisper && (
            <span className="text-[11px] uppercase font-black tracking-widest bg-[#D4B3FF] border-hard-2 rounded-full px-2 py-0.5 flex items-center gap-1">
              fısıltı <button onClick={() => setWhisper(false)} data-testid="clear-whisper" aria-label="clear"><X strokeWidth={3} className="w-3 h-3" /></button>
            </span>
          )}
          {scheduledFor && (
            <span className="text-[11px] uppercase font-black tracking-widest bg-[#FF9E80] border-hard-2 rounded-full px-2 py-0.5 flex items-center gap-1">
              zamanlı: {new Date(scheduledFor).toLocaleString()} <button onClick={() => setScheduledFor(null)} data-testid="clear-schedule" aria-label="clear"><X strokeWidth={3} className="w-3 h-3" /></button>
            </span>
          )}
        </div>
      )}

      <div className={`${hasExtras ? "" : "border-t-[3px] border-[#0A0A0A]"} bg-white p-3 md:p-4 composer-safe flex items-end gap-2 relative shrink-0`}>
        {/* Hidden file input — reused by both mobile and desktop buttons */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,video/*"
          data-testid="composer-file-input"
          className="hidden"
          onChange={handleFileChosen}
        />
        {/* MOBILE compact tool cluster */}
        <div className="flex md:hidden items-center gap-1.5 shrink-0">
          <button
            data-testid="attach-media-btn"
            type="button"
            onClick={openFilePicker}
            disabled={uploading}
            className="w-11 h-11 bg-[#F9F8F6] border-hard rounded-xl flex items-center justify-center press-btn shadow-hard-sm disabled:opacity-50"
            aria-label="Fotoğraf veya video ekle"
            title="Fotoğraf veya video"
          >
            {uploading ? <Loader2 strokeWidth={2.5} className="w-5 h-5 animate-spin" /> : <ImageIcon strokeWidth={2.5} className="w-5 h-5" />}
          </button>
          <div className="relative" ref={pickerRef}>
            <button data-testid="emoji-toggle-btn" type="button" onClick={() => setShowEmoji((s) => !s)}
              className="w-11 h-11 bg-[#F9F8F6] border-hard rounded-xl flex items-center justify-center press-btn shadow-hard-sm" aria-label="Emoji">
              <Smile strokeWidth={2.5} className="w-5 h-5" />
            </button>
            {showEmoji && (
              <div className="fixed left-2 right-2 bottom-24 z-40" data-testid="emoji-picker">
                <div className="border-hard shadow-hard-lg rounded-2xl overflow-hidden bg-white">
                  <EmojiPicker theme={Theme.LIGHT} emojiStyle={EmojiStyle.NATIVE} width="100%" height={340}
                    onEmojiClick={(emoji) => { setText((t) => t + emoji.emoji); inputRef.current?.focus(); setShowEmoji(false); }} />
                </div>
              </div>
            )}
          </div>
          <button
            data-testid="composer-more-btn"
            type="button"
            onClick={() => setMobileToolsOpen(true)}
            className={`w-11 h-11 border-hard rounded-xl flex items-center justify-center press-btn shadow-hard-sm relative ${hasExtras ? "bg-[#CCFF00]" : "bg-[#F9F8F6]"}`}
            aria-label="Daha fazla araç"
          >
            <Plus strokeWidth={3} className="w-5 h-5" />
            {hasExtras && <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-[#FF3B30] border-2 border-white" />}
          </button>
        </div>

        {/* DESKTOP full tool row */}
        <div className="hidden md:flex items-center gap-1.5 shrink-0">
          <button
            data-testid="attach-media-btn-desktop"
            type="button"
            onClick={openFilePicker}
            disabled={uploading}
            className="w-10 h-10 bg-[#F9F8F6] border-hard rounded-xl flex items-center justify-center press-btn shadow-hard-sm disabled:opacity-50"
            aria-label="Fotoğraf veya video ekle"
            title="Fotoğraf veya video"
          >
            {uploading ? <Loader2 strokeWidth={2.5} className="w-4 h-4 animate-spin" /> : <ImageIcon strokeWidth={2.5} className="w-4 h-4" />}
          </button>
          <div className="relative" ref={pickerRef}>
            <button data-testid="emoji-toggle-btn-desktop" type="button" onClick={() => setShowEmoji((s) => !s)}
              className="w-10 h-10 bg-[#F9F8F6] border-hard rounded-xl flex items-center justify-center press-btn shadow-hard-sm" aria-label="Emoji">
              <Smile strokeWidth={2.5} className="w-4 h-4" />
            </button>
            {showEmoji && (
              <div className="absolute bottom-full left-0 mb-2 z-30" data-testid="emoji-picker-desktop">
                <EmojiPicker theme={Theme.LIGHT} emojiStyle={EmojiStyle.NATIVE} width={320} height={380}
                  onEmojiClick={(emoji) => { setText((t) => t + emoji.emoji); inputRef.current?.focus(); }} />
              </div>
            )}
          </div>

          <div className="relative" ref={vibeRef}>
            <button data-testid="vibe-toggle-btn" type="button" onClick={() => setShowVibe((s) => !s)}
              className={`w-10 h-10 border-hard rounded-xl flex items-center justify-center press-btn shadow-hard-sm ${vibe ? "bg-[#CCFF00]" : "bg-[#F9F8F6]"}`} aria-label="Vibe">
              <Palette strokeWidth={2.5} className="w-4 h-4" />
            </button>
            {showVibe && (
              <div className="absolute bottom-full left-0 mb-2 z-30 bg-white border-hard shadow-hard rounded-xl p-3 w-[300px]" data-testid="vibe-panel">
                <div className="text-[11px] uppercase font-black tracking-widest mb-2">Bu mesajın vibe seçimi</div>
                <VibePicker value={vibe} onChange={setVibe} />
              </div>
            )}
          </div>

          <button data-testid="whisper-toggle-btn" type="button" onClick={() => setWhisper((w) => !w)}
            className={`w-10 h-10 border-hard rounded-xl flex items-center justify-center press-btn shadow-hard-sm ${whisper ? "bg-[#D4B3FF]" : "bg-[#F9F8F6]"}`}
            aria-label="Fısıltı modu">
            <Eye strokeWidth={2.5} className="w-4 h-4" />
          </button>

          <button data-testid="schedule-toggle-btn" type="button" onClick={() => setScheduleOpen(true)}
            className={`w-10 h-10 border-hard rounded-xl flex items-center justify-center press-btn shadow-hard-sm ${scheduledFor ? "bg-[#FF9E80]" : "bg-[#F9F8F6]"}`}
            aria-label="Zaman kapsülü">
            <Clock strokeWidth={2.5} className="w-4 h-4" />
          </button>

          <button data-testid="doodle-toggle-btn" type="button" onClick={() => setDoodleOpen(true)}
            className="w-10 h-10 bg-[#F9F8F6] border-hard rounded-xl flex items-center justify-center press-btn shadow-hard-sm" aria-label="Çizim">
            <Sparkles strokeWidth={2.5} className="w-4 h-4" />
          </button>

          <button data-testid="voice-toggle-btn" type="button" onClick={() => setVoiceOpen(true)}
            className="w-10 h-10 bg-[#F9F8F6] border-hard rounded-xl flex items-center justify-center press-btn shadow-hard-sm" aria-label="Sesli">
            <Mic strokeWidth={2.5} className="w-4 h-4" />
          </button>

          <div className="relative" ref={toneRef}>
            <button data-testid="tone-toggle-btn" type="button" onClick={() => setShowTone((s) => !s)}
              disabled={!hasText || toneLoading}
              className="w-10 h-10 bg-[#F9F8F6] border-hard rounded-xl flex items-center justify-center press-btn shadow-hard-sm disabled:opacity-40"
              aria-label="Ton değiştir">
              {toneLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 strokeWidth={2.5} className="w-4 h-4" />}
            </button>
            {showTone && (
              <div className="absolute bottom-full left-0 mb-2 z-30 bg-white border-hard shadow-hard rounded-xl p-2 w-52" data-testid="tone-panel">
                <div className="text-[10px] uppercase font-black tracking-widest px-2 py-1">Yazdığını dönüştür</div>
                {TONES.map((t) => (
                  <button key={t.key} data-testid={`tone-${t.key}`} onClick={() => shiftTone(t.key)}
                    className="w-full text-left px-3 py-2 hover:bg-[#F9F8F6] rounded-lg text-sm font-bold">
                    {t.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-end gap-2 flex-1 w-full min-w-0">
          <textarea
            ref={inputRef}
            data-testid="message-input"
            rows={1}
            value={text}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder="Mesajını yaz…  ipucu: @yappo"
            className="flex-1 min-w-0 resize-none bg-[#F9F8F6] border-hard rounded-xl px-4 py-3 md:py-2.5 focus:outline-none focus:bg-white max-h-32 text-[16px]"
          />

          <button type="button" onClick={handleSend} disabled={!hasText} data-testid="send-message-btn"
            className={`w-12 h-12 shrink-0 border-hard rounded-xl flex items-center justify-center press-btn shadow-hard-sm transition-colors ${hasText ? "bg-[#CCFF00]" : "bg-[#F0F0F0] opacity-60"}`} aria-label="Gönder">
            <Send strokeWidth={3} className="w-5 h-5" />
          </button>
        </div>

        <DoodleCanvas open={doodleOpen} onClose={() => setDoodleOpen(false)} onSubmit={submitDoodle} />
        <VoiceRecorder open={voiceOpen} onClose={() => setVoiceOpen(false)} onSubmit={submitVoice} />
        <ScheduleDialog open={scheduleOpen} onClose={() => setScheduleOpen(false)} onSubmit={(iso) => { setScheduledFor(iso); setScheduleOpen(false); }} />

        {/* Mobile bottom-sheet router */}
        <MobileToolsSheet
          open={mobileToolsOpen}
          onClose={() => setMobileToolsOpen(false)}
          vibe={vibe}
          whisper={whisper}
          scheduledFor={scheduledFor}
          hasText={hasText}
          toneLoading={toneLoading}
          onOpenVibe={() => setMobileVibeOpen(true)}
          onToggleWhisper={() => setWhisper((w) => !w)}
          onOpenSchedule={() => setScheduleOpen(true)}
          onOpenDoodle={() => setDoodleOpen(true)}
          onOpenVoice={() => setVoiceOpen(true)}
          onOpenTone={() => setMobileToneOpen(true)}
        />

        {/* Mobile-only Vibe modal */}
        <AnimatePresence>
          {mobileVibeOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[70] bg-black/50 md:hidden flex items-end"
              onClick={() => setMobileVibeOpen(false)}
              data-testid="mobile-vibe-modal"
            >
              <motion.div
                initial={{ y: "100%" }}
                animate={{ y: 0 }}
                exit={{ y: "100%" }}
                transition={{ type: "spring", damping: 26, stiffness: 260 }}
                className="w-full bg-[#F9F8F6] border-t-[3px] border-[#0A0A0A] rounded-t-3xl p-4 pb-8"
                onClick={(e) => e.stopPropagation()}
                style={{ paddingBottom: "calc(2rem + env(safe-area-inset-bottom, 0px))" }}
              >
                <div className="w-12 h-1.5 bg-[#0A0A0A] rounded-full mx-auto mb-4" />
                <div className="flex items-center justify-between mb-3">
                  <div className="font-display text-xl">Vibe seç</div>
                  <button onClick={() => setMobileVibeOpen(false)} data-testid="mobile-vibe-close" className="w-9 h-9 border-hard-2 rounded-xl flex items-center justify-center bg-white press-btn"><X strokeWidth={3} className="w-4 h-4" /></button>
                </div>
                <VibePicker value={vibe} onChange={(v) => { setVibe(v); setMobileVibeOpen(false); }} />
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Mobile-only Tone modal */}
        <AnimatePresence>
          {mobileToneOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[70] bg-black/50 md:hidden flex items-end"
              onClick={() => setMobileToneOpen(false)}
              data-testid="mobile-tone-modal"
            >
              <motion.div
                initial={{ y: "100%" }}
                animate={{ y: 0 }}
                exit={{ y: "100%" }}
                transition={{ type: "spring", damping: 26, stiffness: 260 }}
                className="w-full bg-[#F9F8F6] border-t-[3px] border-[#0A0A0A] rounded-t-3xl p-4 pb-8"
                onClick={(e) => e.stopPropagation()}
                style={{ paddingBottom: "calc(2rem + env(safe-area-inset-bottom, 0px))" }}
              >
                <div className="w-12 h-1.5 bg-[#0A0A0A] rounded-full mx-auto mb-4" />
                <div className="flex items-center justify-between mb-3">
                  <div className="font-display text-xl">Ton değiştir</div>
                  <button onClick={() => setMobileToneOpen(false)} data-testid="mobile-tone-close" className="w-9 h-9 border-hard-2 rounded-xl flex items-center justify-center bg-white press-btn"><X strokeWidth={3} className="w-4 h-4" /></button>
                </div>
                <div className="text-xs text-[#555] mb-2 font-medium">Yazdığın metni AI ile yeniden yazdır</div>
                <div className="grid grid-cols-2 gap-2">
                  {TONES.map((t) => (
                    <button
                      key={t.key}
                      data-testid={`mobile-tone-${t.key}`}
                      onClick={() => shiftTone(t.key)}
                      disabled={toneLoading}
                      className="border-hard-2 rounded-xl bg-white px-3 py-3 text-sm font-black press-btn shadow-hard-sm disabled:opacity-50"
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
                {toneLoading && <div className="mt-3 text-center text-xs font-bold text-[#555]"><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Dönüştürülüyor…</div>}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  );
}
